import { ChartFigure } from './ChartFigure';
import { fmtK, verticalBars } from './geometry';

/** Longer labels are cut off with an ellipsis so neighbouring bars' labels
 * never overlap; the untruncated name is still on the bar as a native tooltip
 * and in the chart's hidden accessible table. */
const MAX_LABEL_CHARS = 8;

function truncateLabel(label: string): string {
  return label.length > MAX_LABEL_CHARS ? `${label.slice(0, MAX_LABEL_CHARS - 1)}…` : label;
}

export interface BarDatum {
  label: string;
  /** Short label drawn under the bar (first word / abbreviation). */
  shortLabel: string;
  value: number;
}

export interface BarProps {
  data: readonly BarDatum[];
  summary: string;
  /** Optional drill-through when a bar is activated. */
  onSelect?: (datum: BarDatum) => void;
  /** Label-column header for the hidden table. Default `Location`. */
  labelHeader?: string;
  /** Hidden-table caption. Default `Documents by location`. */
  caption?: string;
}

/**
 * §5.4 Vertical bars (by location / by scanner). The leader bar is `--accent`,
 * the rest `--a-bd` — emphasis, not a categorical encoding (§7.3). Bars are
 * keyboard activatable when `onSelect` is given; a hidden table carries all
 * values, its caption/header configurable so a reused chart labels itself right.
 */
export function Bar({
  data,
  summary,
  onSelect,
  labelHeader = 'Location',
  caption = 'Documents by location',
}: BarProps): React.JSX.Element {
  const bars = verticalBars(data.map((d) => d.value));
  return (
    <ChartFigure
      summary={summary}
      columns={[labelHeader, 'Documents']}
      tableCaption={caption}
      rows={data.map((d) => ({ label: d.label, values: { Documents: fmtK(d.value) } }))}
    >
      <svg width="100%" height="132" viewBox="0 0 300 132" fill="none" style={{ display: 'block' }} role="img" aria-label={summary}>
        {bars.map((bar, i) => {
          const datum = data[i];
          if (datum === undefined) return null;
          const fill = bar.leader ? 'var(--accent)' : 'var(--a-bd)';
          const content = (
            <>
              <title>{datum.label}</title>
              <rect x={bar.x} y={bar.y} width="34" height={bar.h} rx="4" fill={fill} />
              <text x={bar.cx} y="124" fontSize="9.5" fill="var(--t2)" textAnchor="middle">
                {truncateLabel(datum.shortLabel)}
              </text>
              <text x={bar.cx} y={bar.vy} fontSize="10" fontWeight="600" fill="var(--t1)" textAnchor="middle">
                {fmtK(datum.value)}
              </text>
            </>
          );
          return onSelect !== undefined ? (
            <g
              key={datum.label}
              role="button"
              tabIndex={0}
              aria-label={`${datum.label}: ${fmtK(datum.value)} documents`}
              style={{ cursor: 'pointer' }}
              onClick={() => { onSelect(datum); }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(datum);
                }
              }}
            >
              {content}
            </g>
          ) : (
            <g key={datum.label}>{content}</g>
          );
        })}
      </svg>
    </ChartFigure>
  );
}
